import { goToHome } from '@/navigation/CSR/goToHome'
import storageMiddleware from '@/utils/storageMiddleware'
import { trackEvent } from '@mercadoni/sinister/dist/cjs/monitors/services'
import { usePathname, useRouter } from 'next/navigation'
import React, { createContext, useCallback, useMemo, useState } from 'react'

const DISPLAYED_RESTRICTED_AGE = 'displayedRestrictedAge'

export interface OnBeforeAddProductProps {
  onAddProduct: () => void
  product: {
    hasAgeRestriction: boolean
  }
}

interface RestrictedAgeContextProps {
  openRestrictedAge: () => void
  onSubmitRestrictedAge: () => void
  openModal: boolean
  closeRestrictedAge: () => void
  getStoredRestrictedAge: () => boolean | null
  onBeforeAddProduct: (props: OnBeforeAddProductProps) => void | null
}

const RestrictedAgeContext = createContext<RestrictedAgeContextProps | null>(
  null
)

export const RestrictedAgeProvider = ({ ...props }) => {
  const [openModal, setOpenModal] = useState(false)
  const [addFunc, setAddFunc] = useState<(() => void) | undefined>()
  const db = useMemo(() => storageMiddleware('local'), [])
  const router = useRouter()
  const pathname = usePathname()

  const restrictedAgeDisplayed = useCallback(
    (value: boolean) => db.setItem(DISPLAYED_RESTRICTED_AGE, String(value)),
    [db]
  )

  const getStoredRestrictedAge = useCallback(
    () => JSON.parse(db.getItem(DISPLAYED_RESTRICTED_AGE)),
    [db]
  )

  const closeRestrictedAge = useCallback(() => {
    setOpenModal(false)
    trackEvent({
      eventName: 'age_restriction_cancelled',
    })
    if (!pathname.includes('store')) {
      goToHome(router)
    }
  }, [pathname, router])

  const openRestrictedAge = useCallback(() => {
    setOpenModal(true)
    trackEvent({
      eventName: 'open_age_restriction_modal',
    })
  }, [])

  const onSubmitRestrictedAge = useCallback(() => {
    setOpenModal(false)
    if (addFunc) {
      addFunc()
      setAddFunc(undefined)
    }
    restrictedAgeDisplayed(true)
    trackEvent({
      eventName: 'age_restriction_accepted',
    })
  }, [addFunc, restrictedAgeDisplayed])

  const onBeforeAddProduct = useCallback(
    ({ onAddProduct, product }: OnBeforeAddProductProps) => {
      const { hasAgeRestriction } = product || {}

      const shouldRequireAgeConfirmation =
        hasAgeRestriction && getStoredRestrictedAge() !== true
      if (shouldRequireAgeConfirmation) {
        setOpenModal(true)
        setAddFunc(() => onAddProduct)
        return
      }

      onAddProduct?.()
    },
    [getStoredRestrictedAge]
  )

  const value = useMemo(
    () => ({
      openRestrictedAge,
      onSubmitRestrictedAge,
      openModal,
      closeRestrictedAge,
      getStoredRestrictedAge,
      onBeforeAddProduct,
    }),
    [
      openRestrictedAge,
      onSubmitRestrictedAge,
      openModal,
      closeRestrictedAge,
      getStoredRestrictedAge,
      onBeforeAddProduct,
    ]
  )

  return (
    <RestrictedAgeContext.Provider value={value}>
      {props.children}
    </RestrictedAgeContext.Provider>
  )
}

export const useRestrictedAgeContext = () => {
  const context = React.useContext(RestrictedAgeContext)
  if (!context) {
    throw new Error('useRestrictedAge: No context')
  }
  return context
}
